import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Form, Modal, Row, Tab } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useClickAway } from 'react-use';
import {
  deleteRequest,
  getRequest,
  postRequest,
} from '../../../utils/axiosRequests';
import NoteDropdown from '../partials/noteDropdown';
import GroupDropdownItem from '../partials/groupDropdownItem';
import ItemWithDeleteIcon from '../partials/itemWithDeleteIcon';
import LoadMore from '../partials/loadMore';
import SearchArea from '../partials/searchAreaAsync';
import TabNav from '../partials/tabNav';
import DynamicItemGroupForm from './dynamicItemGroupForm';

const DynamicPrescriptionItemModal = ({
  itemName,
  modalTitle,
  isOpen,
  setIsOpen,
  selectedItems,
  setSelectedItems,
  selectedGroups,
  setSelectedGroups,
}) => {
  const allNotes = useSelector(
    (state) =>
      state?.prescriptionReducer?.prescriptionInfo?.itemElementNotes || [],
  );
  const [prescriptionItemId, setPrescriptionItemId] = useState(null);
  const [availableElements, setAvailableElements] = useState([]);
  const [elementsInSearch, setElementsInSearch] = useState([]);
  const [elementGroups, setElementGroups] = useState([]);
  const [isGroupModal, setIsGroupModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItem, setTotalItem] = useState(0);
  const perPage = 40;

  console.log('selectedGroups: ', selectedGroups);

  // Resolve the prescriptionItemId from itemName when the modal opens
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setElementsInSearch([]);
      return;
    }
    if (prescriptionItemId) return;

    getRequest('dynamic-prescription-items')
      .then(async (data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const found = list.find(
          (item) => item.name?.toLowerCase() === itemName?.toLowerCase(),
        );
        if (found) {
          setPrescriptionItemId(found.id || found._id);
        } else {
          // Auto-create if the section record doesn't exist yet
          const created = await postRequest('dynamic-prescription-items', {
            name: itemName,
          });
          setPrescriptionItemId(created.id || created._id);
        }
      })
      .catch(console.error);
  }, [isOpen, itemName]);

  // Fetch available elements once prescriptionItemId is known
  useEffect(() => {
    if (!prescriptionItemId) return;
    const url = `dynamic-prescription-items/elements?prescriptionItemId=${prescriptionItemId}&page=${currentPage}&perPage=${perPage}`;
    getRequest(url)
      .then((data) => {
        const items = Array.isArray(data) ? data : data?.data || [];
        if (currentPage > 1) {
          setAvailableElements((prev) => [...prev, ...items]);
        } else {
          setAvailableElements(items);
          setTotalItem(data?.total || items.length);
        }
      })
      .catch(console.error);
  }, [prescriptionItemId, currentPage]);

  // Fetch groups once prescriptionItemId is known and modal is open
  useEffect(() => {
    if (!prescriptionItemId || !isOpen) return;
    getRequest(
      `dynamic-prescription-items/element-groups?prescriptionItemId=${prescriptionItemId}`,
    )
      .then((data) => {
        setElementGroups(Array.isArray(data) ? data : data?.data || []);
      })
      .catch(console.error);
  }, [prescriptionItemId, isOpen]);

  const selectElement = (item) => {
    const isExisting = selectedItems?.some((i) => i.name === item.name);
    setSelectedItems(
      isExisting
        ? selectedItems.filter((i) => i.name !== item.name)
        : [...selectedItems, { name: item.name, date: '', note: '' }],
    );
  };

  const selectElementWithNote = (item) => {
    const isExisting = selectedItems?.some((i) => i.name === item.name);
    setSelectedItems(
      isExisting
        ? selectedItems.map((i) =>
            i.name !== item.name ? i : { ...i, note: item?.note || '' },
          )
        : [
            ...selectedItems,
            { name: item.name, date: '', note: item?.note || '' },
          ],
    );
  };

  const deleteElement = (elementId) => {
    deleteRequest(`dynamic-prescription-items/elements/${elementId}`)
      .then(() => {
        setAvailableElements((prev) =>
          prev.filter((e) => (e.id || e._id) !== elementId),
        );
      })
      .catch(console.error);
  };

  const selectGroup = (group) => {
    const isExist = selectedGroups?.some((g) => g.id === group.id);
    if (!isExist) {
      setSelectedGroups([...selectedGroups, group]);
      const newItems = (group.itemNames || [])
        .filter((name) => !selectedItems.some((i) => i.name === name))
        .map((name) => ({ name, date: '', note: '' }));
      setSelectedItems([...selectedItems, ...newItems]);
    } else {
      setSelectedGroups(selectedGroups.filter((g) => g.id !== group.id));
      const groupNames = new Set(group.itemNames || []);
      setSelectedItems(selectedItems.filter((i) => !groupNames.has(i.name)));
    }
  };

  const deleteGroup = (groupId) => {
    deleteRequest(`dynamic-prescription-items/element-groups/${groupId}`)
      .then(() => {
        setElementGroups((prev) =>
          prev.filter((g) => (g.id || g._id) !== groupId),
        );
      })
      .catch(console.error);
  };

  const removeSelectedItem = (item) => {
    setSelectedItems(selectedItems.filter((i) => i.name !== item.name));
  };

  const handleExtras = (index, fieldName, value) => {
    const updated = [...selectedItems];
    updated[index][fieldName] = value;
    setSelectedItems(updated);
  };

  const handleSearchOrNew = async (selectedOption) => {
    try {
      const { name: selectedData, customOption: isNewOption } =
        selectedOption[0];
      if (isNewOption && prescriptionItemId) {
        const data = await postRequest('dynamic-prescription-items/elements', {
          name: selectedData,
          prescriptionItemId,
        });
        setAvailableElements((prev) => [...prev, data]);
      }
      const isExisting = selectedItems?.some(
        ({ name }) => name?.toLowerCase() === selectedData?.toLowerCase(),
      );
      setSelectedItems(
        isExisting
          ? selectedItems.filter(
              ({ name }) => name?.toLowerCase() !== selectedData?.toLowerCase(),
            )
          : [...selectedItems, { name: selectedData, date: '', note: '' }],
      );
      setSearchQuery('');
    } catch (error) {
      console.error(error);
    }
  };

  const handleOnInputChange = (searchKey) => {
    setIsLoading(true);
    if (searchKey && prescriptionItemId) {
      setElementsInSearch([]);
      const url = `dynamic-prescription-items/elements?prescriptionItemId=${prescriptionItemId}&name=${encodeURIComponent(searchKey)}`;
      setSearchQuery(searchKey);
      getRequest(url)
        .then((data) => {
          setElementsInSearch(Array.isArray(data) ? data : data?.data || []);
          setIsLoading(false);
        })
        .catch(console.error);
    }
  };

  const ref = useRef(null);
  useClickAway(ref, (e) => {
    if (e.target.classList.contains('customize-modal-size')) {
      setIsOpen(false);
    } else if (e.target.classList.contains('child-modal')) {
      setIsGroupModal(false);
    }
  });

  const selectedList = () => {
    const reversed = [...selectedItems].reverse();
    return reversed.map((item, reversedIndex) => {
      const originalIndex = selectedItems.length - 1 - reversedIndex;
      return (
        <Row
          className="selected-item-row"
          key={`${item.name}-${originalIndex}`}
        >
          <Col className="name" lg="3" md="3" sm="12" xs="12">
            {item.name}
          </Col>
          <Col lg="3" md="3" sm="12" xs="12" className="responsive">
            <Form.Control
              type="date"
              size="sm"
              value={item.date || ''}
              onChange={(e) =>
                handleExtras(originalIndex, 'date', e.target.value)
              }
            />
          </Col>
          <Col lg="5" md="5" sm="12" xs="12" className="responsive">
            <NoteDropdown
              value={item.note || ''}
              onChange={(val) => handleExtras(originalIndex, 'note', val)}
              placeholder="Add Note"
              suggestions={allNotes.filter(
                (n) =>
                  n.elementType === itemName &&
                  n.itemElement?.toLowerCase() === item.name?.toLowerCase(),
              )}
            />
          </Col>
          <Col lg="1" md="1" sm="1" xs="2" className="text-end">
            <i
              className="far fa-times-circle pt-1 cursor-pointer"
              aria-hidden="true"
              onClick={() => removeSelectedItem(item)}
            />
          </Col>
        </Row>
      );
    });
  };

  // Transform Redux notes into [{_id: itemElement, notes: [...]}] for PrescriptionSubItemSuggestions
  const elementSuggestions = useMemo(() => {
    const map = {};
    allNotes
      .filter((n) => n.elementType === itemName)
      .forEach((n) => {
        if (!map[n.itemElement]) map[n.itemElement] = new Set();
        map[n.itemElement].add(n.note);
      });
    return Object.entries(map).map(([id, notesSet]) => ({
      _id: id,
      notes: [...notesSet],
    }));
  }, [allNotes, itemName]);

  const allElements = () =>
    availableElements.map((item, index) => {
      const isSelected = selectedItems?.some((i) => i.name === item.name);
      return (
        <ItemWithDeleteIcon
          key={index}
          item={item}
          isSelected={isSelected}
          itemClickAction={selectElement}
          removeClickAction={() => deleteElement(item.id || item._id)}
          suggestions={elementSuggestions}
          selectItemWithNote={selectElementWithNote}
        />
      );
    });

  const groupList = elementGroups.map((group, index) => {
    const isSelected = selectedGroups?.some((g) => g.id === group.id);
    return (
      <GroupDropdownItem
        key={index}
        group={group}
        isSelected={isSelected}
        onSelect={selectGroup}
        onDelete={(id) => deleteGroup(id || group._id)}
        selectedItems={selectedItems}
        onToggleItem={(itemName) => selectElement({ name: itemName })}
      />
    );
  });

  return (
    <Modal show={isOpen} size="lg" className="customize-modal-size">
      <Modal.Body ref={ref}>
        <SearchArea
          modalTitle={modalTitle}
          closeAction={setIsOpen}
          setSearchOptions={setElementsInSearch}
          handleOnInputChange={handleOnInputChange}
          handleSearchOrNew={handleSearchOrNew}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          options={elementsInSearch}
          selectedInputs={selectedItems}
          placeholder={modalTitle}
        />
        <Tab.Container id={`dynamic-tabs-${itemName}`} defaultActiveKey="all">
          <TabNav action={setIsGroupModal} selectedItems={selectedItems} />
          <Tab.Content>
            <Tab.Pane eventKey="all" className="add-scroll">
              <Row className="complains-area mr-0 ml-0">{allElements()}</Row>
              <LoadMore
                currentPage={currentPage}
                totalItem={totalItem}
                perPage={perPage}
                currentPageAction={setCurrentPage}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="group" className="add-scroll">
              <Row className="complains-area mr-0 ml-0 mt-1">{groupList}</Row>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {prescriptionItemId && (
          <DynamicItemGroupForm
            modalTitle={modalTitle}
            isGroupModal={isGroupModal}
            setIsGroupModal={setIsGroupModal}
            prescriptionItemId={prescriptionItemId}
            itemNames={selectedItems.map((i) => i.name)}
            elementGroups={elementGroups}
            setElementGroups={setElementGroups}
            selectedGroups={selectedGroups}
            setSelectedGroups={setSelectedGroups}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        )}

        <hr className="selected-hr" />
        <div className="selected-item-title">Selected list</div>
        <div className="selected-item-area">{selectedList()}</div>
      </Modal.Body>
    </Modal>
  );
};

export default memo(DynamicPrescriptionItemModal);
